import { getTheme, type Theme } from './lib/theme';
import type { GitCommit } from './types';

/**
 * Live Git graph — geometry and layout (brief, section 12).
 *
 * This module is pure maths and text measurement, plus the one ramp of colour
 * that cannot live in a stylesheet: it decides which lane each commit sits in,
 * where that lane lands in SVG space, how long a subject line may be, and what
 * shade a lane is drawn in. Nothing here touches the DOM. The drawing lives in
 * components/play/GitGraph.tsx, which animates commits between the positions
 * this file computes — that is what makes a rebase or a reset legible: the
 * commits move rather than blink.
 *
 * Lane assignment is the classic "reserved column" walk: each commit takes the
 * column already reserved for it by an earlier child, or the leftmost free
 * one, then reserves a column for each of its parents. Commits arrive newest
 * first, so row order is simply the order of `commits`.
 */

export const ROW = 40;
export const LANE = 26;
export const PAD_LEFT = 26;
export const PAD_TOP = 22;
export const NODE_R = 6;

/**
 * Achromatic lane ramp: lanes are told apart by lightness, never hue. Lane 0 —
 * the primary lane, usually the branch you are on — is the one that reads
 * first; the rest step down and back up so that two adjacent lanes stay
 * separable against the pane they are drawn on.
 *
 * Two ramps, because "brightest" means the opposite thing on the two grounds
 * the pane can have. The light ramp is not the dark one inverted arithmetic-
 * ally: each entry is the colour that holds the *same contrast* against a
 * white pane that its opposite number holds against a near-black one, so lane
 * 4 stays exactly as easy to tell from lane 1 as it ever was. Same reasoning
 * as the ink ramp in styles/theme.css, and the same arithmetic.
 *
 * Colour in a TypeScript module rather than in CSS because these are SVG
 * presentation attributes — `fill={color}` — and a presentation attribute does
 * not resolve `var()`. Turning six fills into six classes to get them into the
 * stylesheet would move the ramp without moving the decision.
 */
export const LANE_COLORS: Record<Theme, readonly string[]> = {
  dark: ['#ffffff', '#a8a8a8', '#6f6f6f', '#d0d0d0', '#8a8a8a', '#585858'],
  light: ['#000000', '#4f4f4f', '#878787', '#333333', '#696969', '#ababab'],
};

export interface Pos {
  row: number;
  lane: number;
}

export function layout(commits: GitCommit[]): { pos: Map<string, Pos>; laneCount: number } {
  const pos = new Map<string, Pos>();
  const lanes: (string | null)[] = [];
  const freeLane = () => {
    let l = lanes.indexOf(null);
    if (l === -1) {
      l = lanes.length;
      lanes.push(null);
    }
    return l;
  };

  commits.forEach((c, row) => {
    let lane = lanes.indexOf(c.hash);
    if (lane === -1) lane = freeLane();
    lanes.forEach((h, i) => {
      if (h === c.hash && i !== lane) lanes[i] = null;
    });
    pos.set(c.hash, { row, lane });

    const [first, ...rest] = c.parents;
    lanes[lane] = first !== undefined && !lanes.includes(first) ? first : null;
    for (const p of rest) {
      if (!lanes.includes(p)) lanes[freeLane()] = p;
    }
  });
  return { pos, laneCount: Math.max(1, lanes.length) };
}

/** Centre of a lane, in SVG user units. */
export const laneX = (lane: number): number => PAD_LEFT + lane * LANE;

/** Centre of a row, in SVG user units. */
export const rowY = (row: number): number => PAD_TOP + row * ROW;

/** Where the ref pills and the subject start, clear of the widest lane. */
export const labelXFor = (laneCount: number): number => PAD_LEFT + laneCount * LANE + 4;

/**
 * The colour of one lane, on the ground the page is currently using.
 *
 * The theme is a parameter with a default rather than a read, so that the
 * component which has already subscribed to it can pass what it has — and so
 * that a test can ask for either ramp without a document to ask.
 */
export const laneColor = (lane: number, theme: Theme = getTheme()): string => {
  const ramp = LANE_COLORS[theme];
  return ramp[lane % ramp.length]!;
};

/**
 * The edge from a commit down to one of its parents. A straight drop while the
 * lane holds; a cubic that leans across when the parent lives elsewhere.
 *
 * Both cases are emitted as a cubic even though the straight one does not need
 * to be. When a lane changes, the animation interpolates between the old and
 * new path strings, and a mixer can only do that when both have the same shape:
 * a line growing into a curve would otherwise snap. With the control points
 * collapsed onto the endpoints the cubic draws exactly the straight line it
 * replaces, so nothing looks different and every edge can morph.
 */
export function edgePath(x1: number, y1: number, x2: number, y2: number): string {
  const mid = (y1 + y2) / 2;
  return `M${x1} ${y1} C${x1} ${mid} ${x2} ${mid} ${x2} ${y2}`;
}

/**
 * The length of the edge `edgePath` draws between the same four numbers,
 * rounded up. A landing edge is dashed to its own length and drawn in by
 * sliding the dash, and that needs the number without measuring the DOM: the
 * cubic is sampled and its chords summed. A chord sum falls a little short of
 * the curve, so the result is padded before it is rounded, on the side that
 * costs nothing — a dash longer than the path still covers it, one shorter
 * leaves a gap at the parent.
 */
export function edgeLength(x1: number, y1: number, x2: number, y2: number): number {
  const mid = (y1 + y2) / 2;
  const steps = 20;
  let length = 0;
  let px = x1;
  let py = y1;
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    const u = 1 - t;
    // The same control points edgePath emits: (x1, mid) and (x2, mid).
    const x = u * u * u * x1 + 3 * u * u * t * x1 + 3 * u * t * t * x2 + t * t * t * x2;
    const y = u * u * u * y1 + 3 * u * u * t * mid + 3 * u * t * t * mid + t * t * t * y2;
    length += Math.hypot(x - px, y - py);
    px = x;
    py = y;
  }
  return Math.ceil(length * 1.02) + 1;
}

export function truncate(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, Math.max(0, max - 1))}…` : s;
}

/**
 * Width of a ref pill, estimated from the label inside it.
 *
 * The estimate used to be one number per character, which was right while
 * every pill held a branch name in Space Mono. Two of them now hold prose —
 * `unreachable` and its Arabic — and Arabic is not monospaced: its letters
 * are narrower than the mono cell and they join, so `غير قابل للوصول` counted
 * at 6.6px a character comes out about 40% too wide and the pill floats away
 * from its label.
 *
 * So the advance is measured per script rather than per string: 6.6 for the
 * mono cell, 4.4 for Arabic. Still an estimate — the real answer needs a
 * measurement the SVG cannot cheaply make — but one that is wrong by a pixel
 * either way instead of by a third of the pill.
 */
const ARABIC = /[؀-ۿݐ-ݿﭐ-﷿ﹰ-﻿]/;

export const pillWidth = (text: string): number => {
  let width = 0;
  for (const ch of text) width += ARABIC.test(ch) ? 4.4 : 6.6;
  return width + 14;
};

/** How many characters of subject survive between the pills and the hash column. */
export const subjectBudget = (width: number, from: number): number =>
  Math.max(8, Math.floor((width - from - 96) / 6.8));
